import { atom, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export interface SearchState {
  terms: string[];
}

const DEFAULT_STATE: SearchState = {
  terms: [],
};

/** Drop legacy pagination keys from pre–PATHWAY-3590 `app.search` blobs. */
export const readSearchState = (raw: unknown): SearchState => {
  if (
    typeof raw === 'object' &&
    raw !== null &&
    Array.isArray((raw as SearchState).terms)
  ) {
    return { terms: (raw as SearchState).terms };
  }
  return DEFAULT_STATE;
};

const writeSearchState = (terms: string[]): SearchState => ({ terms });

export const searchBaseAtom = atomWithStorage('app.search', DEFAULT_STATE);

const base = searchBaseAtom;

export const searchAtom = atom(
  get => readSearchState(get(base)),
  (get, set, patch: Partial<SearchState>) => {
    const current = readSearchState(get(base));
    set(base, writeSearchState(patch.terms ?? current.terms));
  },
);

type Action =
  | { type: 'removeSearchTerm'; payload: string }
  | { type: 'resetSearchState' }
  | { type: 'saveSearchTerm'; payload: string };

export const searchDispatchAtom = atom(
  get => readSearchState(get(base)),
  (get, set, action: Action) => {
    const { terms: prevTerms } = readSearchState(get(base));
    switch (action.type) {
      case 'removeSearchTerm':
        set(
          base,
          writeSearchState(prevTerms.filter(term => term !== action.payload)),
        );
        return;
      case 'resetSearchState':
        set(base, DEFAULT_STATE);
        return;
      case 'saveSearchTerm': {
        const termExistsIndex = prevTerms.findIndex(
          term => action.payload === term,
        );
        if (termExistsIndex >= 0) {
          if (termExistsIndex === 0) {
            return;
          }
          const termsWithExistingTermRemoved = prevTerms.filter(
            (_term, idx) => idx !== termExistsIndex,
          );
          set(
            base,
            writeSearchState([action.payload, ...termsWithExistingTermRemoved]),
          );
        } else {
          set(
            base,
            writeSearchState([action.payload, ...prevTerms].slice(0, 5)),
          );
        }
        return;
      }
    }
  },
);

export const useSearchDispatch = () => {
  const setDispatch = useSetAtom(searchDispatchAtom);
  return {
    saveTerm: (term: string) => {
      setDispatch({ type: 'saveSearchTerm', payload: term });
    },
    removeTerm: (term: string) => {
      setDispatch({ type: 'removeSearchTerm', payload: term });
    },
    reset: () => {
      setDispatch({ type: 'resetSearchState' });
    },
  };
};
